// src/stores/useTagStore.js
import { defineStore } from "pinia";
import * as tagService from "@/services/tagService";

export const useTagStore = defineStore("tag", {
  state: () => ({
    tags: [],
    selectedTagIds: [],
    idTag: null,
    selectedTag: null, // ✅ ADICIONAR
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "", // 🔍 novo campo para busca
    shouldForcePageOne: false,
  }),

  getters: {
    isAllSelected: (state) => (total) => {
      return state.selectedTagIds.length === total;
    },
  },

  actions: {
    setIdTag(id) {
      this.idTag = id;
    },

    toggleSelection(id) {
      const index = this.selectedTagIds.indexOf(id);
      if (index > -1) {
        this.selectedTagIds.splice(index, 1);
      } else {
        this.selectedTagIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedTagIds = [...ids];
    },

    clearSelection() {
      this.selectedTagIds = [];
    },

    async fetchTags() {
      try {
        //alert("fetchTags 1");
        const response = await tagService.fetchTags();
        //alert("fetchTags 2");
        this.tags = response.data;
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar tags de nota:", this.error);
      }
    },

    async getTagById(id) {
      try {
        const response = await tagService.getTagById(id);
        this.selectedTag = response.data; // ✅ guarda no estado
        return response.data;
      } catch (error) {
        // APLIQUE A MESMA LÓGICA DE EXTRAÇÃO AQUI E NOS OUTROS CATCHES
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        console.error("❌ Erro ao obter tag de nota:", mensagem);
        throw new Error(mensagem);
      }
    },

    async createTag(data) {
      try {
        const createdTag = await tagService.createTag(data);
        this.shouldForcePageOne = true;
        await this.fetchTagsPaged(1, this.pageSize, this.searchTerm);
        // 🚀 CORREÇÃO: Retorna o PAYLOAD de dados puro, e não o objeto de resposta completo.
        return createdTag.data;
      } catch (error) {
        console.error("❌ Erro ao cadastrar tag de nota:", error.mensagem);
        throw error;
      }
    },

    // DEPOIS
    async updateTag(data) {
      try {
        await tagService.updateTag(data);

        // Atualiza tagStore.tags (lista paginada)
        const index = this.tags.findIndex((a) => a.tagId === data.tagId);
        if (index !== -1) {
          this.tags[index] = { ...data };
        }

        // >>> Propaga nome e cores para todas as notas que usam esta tag
        const { useNoteStore } = await import("./useNoteStore");
        const noteStore = useNoteStore();

        const applyToNote = (note) => {
          const tagIndex = (note.tagIds ?? []).indexOf(data.tagId);
          if (tagIndex === -1) return;

          if (Array.isArray(note.tagNames))
            note.tagNames[tagIndex] = data.tagName;
          if (Array.isArray(note.tagSpecificColors))
            note.tagSpecificColors[tagIndex] = data.specificColor;
          if (Array.isArray(note.tagSpecificBackgrounds))
            note.tagSpecificBackgrounds[tagIndex] = data.specificBackground;
          if (Array.isArray(note.tagSpecificUnderlines))
            note.tagSpecificUnderlines[tagIndex] = data.specificUnderline;
        };

        noteStore.allNotes.forEach(applyToNote);
        noteStore.notes.forEach(applyToNote);
      } catch (error) {
        console.error("❌ Erro ao atualizar tag de nota:", error.mensagem);
        throw error;
      }
    },

    async deleteTagById(id) {
      try {
        await tagService.deleteTag(id);
        await this.fetchTagsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        if (this.tags.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchTagsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        // APLIQUE A MESMA LÓGICA DE EXTRAÇÃO AQUI
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        console.error("❌ Erro ao excluir tag de nota:", mensagem);
        throw new Error(mensagem);
      }
    },

    async deleteSelectedTags() {
      if (!this.selectedTagIds.length) return;

      try {
        await tagService.deleteTagsBatch(this.selectedTagIds);
        this.clearSelection();
        await this.fetchTagsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        if (this.tags.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchTagsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        // APLIQUE A MESMA LÓGICA DE EXTRAÇÃO AQUI
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        console.error("❌ Erro ao excluir tags de nota em lote:", mensagem);
        throw new Error(mensagem);
      }
    },

    async fetchTagsPaged(pageNumber, pageSize, search) {
      try {
        const response = await tagService.fetchTagsPaged(
          pageNumber,
          pageSize,
          search,
        );
        this.tags = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
      } catch (error) {
        // APLIQUE A MESMA LÓGICA DE EXTRAÇÃO AQUI
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar tags de nota paginadas:", mensagem);
        throw new Error(mensagem);
      }
    },

    setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1; // 🔁 redefine a página
      this.shouldForcePageOne = true; // 🔁 aciona o highlight no BottomPagination
      this.fetchTagsPaged(1, this.pageSize, term); // 🔍 dispara a busca
    },
  },
});
